
import { useQuery } from '@tanstack/react-query';
import { AIStaffCandidate, AIStaffRole, AIStaffMember } from '@/lib/types';

// This is a simple hook to provide AI staff candidate data for the hiring process
export function useStaff() {
  // Return a set of predefined AI staff candidates
  return useQuery({
    queryKey: ['aiCandidates'],
    queryFn: () => {
      // Return mock data for available AI staff candidates
      const candidates: AIStaffCandidate[] = [
        {
          id: 1,
          name: "Marketing Assistant",
          role: "marketing",
          level: "intern",
          avatar: "📊",
          description: "Specializes in creating and optimizing marketing content across all channels.",
          abilities: ["Social Media", "Email Marketing", "Content Creation", "SEO", "Analytics"]
        },
        {
          id: 2,
          name: "Sales Assistant",
          role: "sales",
          level: "intern",
          avatar: "💼",
          description: "Automates lead generation and sales outreach processes.",
          abilities: ["Lead Generation", "CRM Automation", "Email Outreach", "Pipeline Management"]
        },
        {
          id: 3,
          name: "Customer Support Assistant",
          role: "customer-support",
          level: "intern",
          avatar: "🎧",
          description: "Handles customer inquiries and support tickets efficiently.",
          abilities: ["Auto-responses", "Ticket Management", "FAQ Knowledge", "Customer Satisfaction"]
        },
        {
          id: 4,
          name: "Data Entry Assistant",
          role: "data-entry",
          level: "intern",
          avatar: "📝",
          description: "Automates data entry tasks and form processing.",
          abilities: ["Form Filling", "Data Extraction", "Document Processing", "Data Validation"]
        }
      ];
      
      return candidates;
    },
    staleTime: Infinity // This data doesn't change, so we can cache it indefinitely
  });
}

// Re-export with the name expected by other components
export const useAllStaff = () => {
  const { data = [] } = useStaff();
  
  // Create a basic stub implementation that resembles AIStaffMember objects
  // This avoids type errors when we need to cast between types
  const createStubStaffMember = (candidate: AIStaffCandidate): AIStaffMember => ({
    id: String(candidate.id),
    name: candidate.name,
    role: candidate.role,
    level: candidate.level,
    experience: 0,
    skillPoints: 0,
    abilities: candidate.abilities || [],
    avatar: candidate.avatar,
    hiredAt: new Date(),
    achievements: [],
    is_active: true,
  });
  
  // Convert candidates to staff members for compatibility
  const staffMembers = data.map(createStubStaffMember);
  
  return {
    allAIStaff: staffMembers,
    isRoleAlreadyOnboarded: (role: string) => false, // Stub implementation
    getTerminatedStaffByRole: (role: string) => null, // Stub implementation
    fetchAllAIStaff: async () => staffMembers,
    isLoading: false,
    error: null
  };
};
